import React, { useRef } from "react";
import cn from "classnames";
import { css } from "@emotion/react";
import { useGlobalKeyListener } from "../../../hooks/use-global-key-listener";
import { ModalRootPortalEntrance } from "../technical/ModalRootPortalEntrance";
import { CSS_MQ, CSS_MQ_ONLY } from "../../../styles/breakpoints";
import { CloseButton } from "./CloseButton";
import type { ImageWithFocalPointType, VideoType } from "@swo/shared";
import { ContentfulImage2 } from "../image-related/ContentfulImage2";
import { useClickOutside } from "../../../hooks/use-click-outside";
import { SwarovskiOptikVideoPlayer } from "../../VideoComponent/VideoComponent";

type Visual = ImageWithFocalPointType | VideoType | null;

type Props = React.PropsWithChildren<{
  modalIsOpen: boolean;
  hideModalCallback: () => void;
  visual: {
    square: Visual;
    horizontal: Visual;
  };
}>;

export const SwarovskiModalWithVisualWrapper: React.FC<Props> = ({
  modalIsOpen,
  hideModalCallback,
  visual,
  children,
}) => {
  const divRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(divRef, hideModalCallback, true);

  useGlobalKeyListener("Escape", hideModalCallback, [hideModalCallback], modalIsOpen);

  const wrapperStyles = css`
    width: var(--max-content-width-new);
    max-width: 100%;

    ${CSS_MQ_ONLY.sm} {
      width: 360px;
    }

    ${CSS_MQ_ONLY.md} {
      width: 500px;
    }

    ${CSS_MQ.atLeastLg} {
      &.with-image {
        width: 960px;
      }

      &:not(.with-image) {
        width: 500px;
      }
    }

    // arbitrary margin
    --margin: calc(var(--header-unfixed-height) + 60px);
    ${CSS_MQ.atLeastLg} {
      // arbitrary margin
      --margin: calc(var(--header-unfixed-height));
    }
    ${CSS_MQ.atLeastXl} {
      // arbitrary margin
      --margin: calc(var(--header-unfixed-height) + 40px);
    }

    ${CSS_MQ.lowResNotebook} {
      --margin: calc(var(--header-unfixed-height) + 10px);
    }

    @media (max-height: 800px) {
      --margin: calc(var(--header-unfixed-height) + 10px);
    }

    // Duplicated to serve as a fallback
    max-height: calc(100vh - var(--margin) - 10px);
    max-height: calc(100dvh - var(--margin) - 10ox);

    overflow-y: auto;

    margin-top: var(--margin);
    margin-left: auto;
    margin-right: auto;

    background-color: var(--so-green-white);

    position: relative;
  `;

  const hasAsset = !!visual.square || !!visual.horizontal;

  return (
    <ModalRootPortalEntrance>
      <div
        className={cn(modalIsOpen ? "visible" : "hidden")}
        css={css`
          &.hidden {
            display: none;
          }

          &.visible {
          }

          height: 100%;
          width: 100%;
          position: fixed;

          z-index: 110;
          background-color: rgba(0, 0, 0, 0.75);
        `}
      >
        <div ref={divRef} css={wrapperStyles} className={cn("js-modal-wrapper", hasAsset ? "with-image" : undefined)}>
          <CloseButton onClick={hideModalCallback} withEnhandedBackground={!!hasAsset} />

          <div
            className={cn(hasAsset ? "with-image" : undefined)}
            css={css`
              display: grid;

              ${CSS_MQ.atMostMd} {
                grid-template-columns: 1fr;
              }

              ${CSS_MQ.atLeastLg} {
                &.with-image {
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                }
              }
            `}
          >
            <NewsletterVisual mobile={visual.horizontal} desktop={visual.square} />

            <div
              css={css`
                padding: 40px 24px 30px;

                ${CSS_MQ.atLeastSm} {
                  padding: 30px 30px;
                }

                ${CSS_MQ.atLeastMd} {
                  padding: 36px 48px;
                }

                ${CSS_MQ.atLeastLg} {
                  &.with-image {
                    aspect-ratio: 1/1;
                  }
                }
              `}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </ModalRootPortalEntrance>
  );
};

const NewsletterVisual = ({ mobile, desktop }: { mobile: Visual; desktop: Visual }) => {
  return (
    <>
      {mobile ? (
        <div
          css={css`
            aspect-ratio: 16/9;

            ${CSS_MQ.atLeastLg} {
              display: none;
            }

            position: relative;
            display: flex;
          `}
        >
          <ImageOrVideo visual={mobile} />
        </div>
      ) : null}

      {desktop ? (
        <div
          css={css`
            display: none;

            ${CSS_MQ.atLeastLg} {
              aspect-ratio: 1/1;
              display: flex;
            }

            position: relative;
          `}
        >
          <ImageOrVideo visual={desktop} />
        </div>
      ) : null}
    </>
  );
};

const ImageOrVideo = ({ visual }: { visual: NonNullable<Visual> }) => {
  switch (visual.__typename) {
    case "UtilityImageWithFocalPoint":
      return (
        <ContentfulImage2
          assetId={visual.image.sys.id}
          contentfulUrl={visual.image.url}
          withTransparency={false}
          sizes="400px"
          cropping={visual}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
          `}
        />
      );
    case "UtilityVideo": {
      const { video } = visual;

      function getData() {
        const requestedWidth = 500;

        const bestVideo = [...video.derived_formats.mp4.variants].sort(
          (a, b) => Math.abs(requestedWidth - a.width) - Math.abs(requestedWidth - b.width),
        )[0];

        if (!bestVideo) {
          return {
            url: video.secure_url,
            poster: video.poster.url,
            format: video.format,
          };
        }

        return {
          url: bestVideo.video.url,
          poster: bestVideo.poster,
          format: bestVideo.video.type,
        };
      }

      const { url, poster, format } = getData();

      return (
        <video
          poster={poster}
          autoPlay
          muted
          playsInline
          loop
          preload="auto"
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
          `}
        >
          <source src={url} type={format} />
        </video>
      );
    }
  }
};
